import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Filter, UserCheck, UserX, Clock, 
  GraduationCap, Edit2, Trash2, ChevronDown, Check, X,
  User, Mail, Phone, Calendar, FileText, Save, Download, MapPin, AlertCircle
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const STATUS_OPTIONS = [
  { value: 'actif', label: 'Actif', color: 'bg-green-100 text-green-700' },
  { value: 'en_attente', label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'suspendu', label: 'Suspendu', color: 'bg-red-100 text-red-700' },
  { value: 'termine', label: 'Terminé', color: 'bg-gray-100 text-gray-700' }
];

const NIVEAU_OPTIONS = [
  'Pre A1', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'
];

const AdminEleves = () => {
  const [eleves, setEleves] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEleve, setSelectedEleve] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    groupe_id: '',
    niveau: '',
    sans_groupe: false
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadEleves, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const loadData = async () => {
    try {
      const [groupesRes, statsRes] = await Promise.all([
        api.get('/groupes'),
        api.get('/users/eleves/stats/by-status')
      ]);
      setGroupes(groupesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadEleves = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.groupe_id) params.append('groupe_id', filters.groupe_id);
      if (filters.niveau) params.append('niveau', filters.niveau);
      if (filters.sans_groupe) params.append('sans_groupe', 'true');
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/users/eleves?${params.toString()}`);
      setEleves(response.data);
    } catch (error) {
      console.error('Error loading eleves:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectEleve = async (eleve) => {
    setSelectedEleve(eleve);
    setEditData({
      status: eleve.status || 'actif',
      niveau_cambridge: eleve.niveau_cambridge || '',
      groupe_id: eleve.groupe_id || '',
      notes: eleve.notes || '',
      telephone: eleve.telephone || ''
    });
    setEditMode(false);
    // Fetch full details (parents, address, inscription) in background
    try {
      const res = await api.get(`/users/eleves/${eleve.id}`);
      setSelectedEleve(res.data);
    } catch (error) {
      // keep basic eleve data
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.groupe_id) params.append('groupe_id', filters.groupe_id);
      if (filters.niveau) params.append('niveau', filters.niveau);
      if (filters.sans_groupe) params.append('sans_groupe', 'true');
      const res = await api.get(`/users/eleves/export/csv?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `eleves_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export téléchargé', description: `${eleves.length} élève(s) exporté(s).` });
    } catch (error) {
      toast({ title: 'Erreur', description: "Impossible d'exporter la liste", variant: 'destructive' });
    }
  };

  const saveEleve = async () => {
    if (!selectedEleve) return;
    setSaving(true);
    try {
      await api.put(`/users/eleves/${selectedEleve.id}`, editData);
      toast({ title: 'Élève mis à jour', description: 'Les modifications ont été enregistrées' });
      loadEleves();
      loadData();
      // Update selected eleve with new data
      setSelectedEleve({ ...selectedEleve, ...editData, groupe: groupes.find(g => g.id === editData.groupe_id) || null });
      setEditMode(false);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const removeFromGroupe = async (eleveId) => {
    if (!window.confirm('Retirer cet élève du groupe ?')) return;
    try {
      await api.delete(`/users/eleves/${eleveId}/groupe`);
      toast({ title: 'Élève retiré du groupe' });
      loadEleves();
      loadData();
      if (selectedEleve?.id === eleveId) {
        setSelectedEleve({ ...selectedEleve, groupe_id: null, groupe: null });
        setEditData({ ...editData, groupe_id: '' });
      }
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    );
  };

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) => k !== 'search' && v).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-eleves">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Gestion des élèves</h1>
            <p data-testid="eleves-count" className="text-[#6777A1] mt-1">{eleves.length} élève(s) trouvé(s)</p>
          </div>
          <Button
            onClick={exportCsv}
            disabled={eleves.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-eleves-csv"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter en CSV
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-[#2C3C65]">{stats.total}</p>
              <p className="text-xs text-[#6777A1]">Total</p>
            </div>
            <div className="bg-green-50 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.actif}</p>
              <p className="text-xs text-green-700">Actifs</p>
            </div>
            <div className="bg-yellow-50 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.en_attente}</p>
              <p className="text-xs text-yellow-700">En attente</p>
            </div>
            <div className="bg-red-50 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.suspendu}</p>
              <p className="text-xs text-red-700">Suspendus</p>
            </div>
            <div className="bg-gray-50 rounded-xl shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-gray-600">{stats.termine}</p>
              <p className="text-xs text-gray-700">Terminés</p>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Rechercher par nom, prénom ou email..."
                className="pl-10"
                data-testid="eleves-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={activeFiltersCount > 0 ? 'border-[#285BBB] text-[#285BBB]' : ''}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t grid md:grid-cols-4 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Statut</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                  data-testid="filter-status"
                >
                  <option value="">Tous les statuts</option>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Groupe</label>
                <select
                  value={filters.groupe_id}
                  onChange={(e) => setFilters({ ...filters, groupe_id: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                  data-testid="filter-groupe"
                >
                  <option value="">Tous les groupes</option>
                  {groupes.map(g => (
                    <option key={g.id} value={g.id}>{g.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Niveau Cambridge</label>
                <select
                  value={filters.niveau}
                  onChange={(e) => setFilters({ ...filters, niveau: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                  data-testid="filter-niveau"
                >
                  <option value="">Tous les niveaux</option>
                  {NIVEAU_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.sans_groupe}
                    onChange={(e) => setFilters({ ...filters, sans_groupe: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-[#2C3C65]">Sans groupe uniquement</span>
                </label>
              </div>
              <div className="md:col-span-4">
                <button
                  onClick={() => setFilters({ search: '', status: '', groupe_id: '', niveau: '', sans_groupe: false })}
                  className="text-sm text-[#285BBB] hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Eleves List */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b bg-[#F5F7FF]">
              <h2 className="font-semibold text-[#2C3C65]">Liste des élèves</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-[#6777A1]">Chargement...</div>
            ) : eleves.length === 0 ? (
              <div data-testid="eleves-empty-state" className="p-8 text-center text-[#6777A1]">Aucun élève trouvé</div>
            ) : (
              <div data-testid="eleves-list" className="divide-y max-h-[600px] overflow-y-auto">
                {eleves.map((eleve) => (
                  <div
                    key={eleve.id}
                    data-testid={`eleve-row-${eleve.id}`}
                    onClick={() => selectEleve(eleve)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedEleve?.id === eleve.id ? 'bg-blue-50 border-l-4 border-[#285BBB]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-[#285BBB] rounded-full flex items-center justify-center text-white font-semibold mr-3">
                          {eleve.prenom?.[0]}{eleve.nom?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-[#2C3C65]">{eleve.prenom} {eleve.nom}</p>
                          <p className="text-xs text-[#6777A1]">
                            {eleve.groupe?.nom || 'Non affecté'}
                            {eleve.classe ? ` · ${eleve.classe}` : ''}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(eleve.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Eleve Details */}
          <div className="lg:col-span-2">
            {selectedEleve ? (
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-4 border-b bg-[#F5F7FF] flex justify-between items-center">
                  <h2 className="font-semibold text-[#2C3C65]">
                    {selectedEleve.prenom} {selectedEleve.nom}
                  </h2>
                  <div className="flex gap-2">
                    {editMode ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditMode(false)}
                        >
                          <X className="h-4 w-4 mr-1" /> Annuler
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveEleve}
                          disabled={saving}
                          className="bg-[#285BBB] hover:bg-[#2C3C65] text-white"
                        >
                          <Save className="h-4 w-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setEditMode(true)}
                        className="bg-[#285BBB] hover:bg-[#2C3C65] text-white"
                      >
                        <Edit2 className="h-4 w-4 mr-1" /> Modifier
                      </Button>
                    )}
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="h-5 w-5 text-[#285BBB]" />
                      <div>
                        <p className="text-xs text-[#6777A1]">Email</p>
                        <p data-testid="eleve-detail-email" className="text-sm font-medium text-[#2C3C65]">
                          {selectedEleve.email || (selectedEleve.no_login ? 'Pas de compte (fiche créée depuis l\'inscription)' : 'Non renseigné')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="h-5 w-5 text-[#285BBB]" />
                      <div>
                        <p className="text-xs text-[#6777A1]">Téléphone</p>
                        {editMode ? (
                          <Input
                            value={editData.telephone}
                            onChange={(e) => setEditData({ ...editData, telephone: e.target.value })}
                            placeholder="06 XX XX XX XX"
                            className="h-8 text-sm"
                          />
                        ) : (
                          <p className="text-sm font-medium text-[#2C3C65]">
                            {selectedEleve.telephone || 'Non renseigné'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status and Level */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Statut</label>
                      {editMode ? (
                        <select
                          value={editData.status}
                          onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                          className="w-full rounded-md border border-gray-300 p-2"
                          data-testid="edit-status"
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      ) : (
                        getStatusBadge(selectedEleve.status)
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Niveau Cambridge</label>
                      {editMode ? (
                        <select
                          value={editData.niveau_cambridge}
                          onChange={(e) => setEditData({ ...editData, niveau_cambridge: e.target.value })}
                          className="w-full rounded-md border border-gray-300 p-2"
                          data-testid="edit-niveau"
                        >
                          <option value="">Non défini</option>
                          {NIVEAU_OPTIONS.map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="px-3 py-1 bg-[#285BBB] text-white rounded-full text-sm">
                          {selectedEleve.niveau_cambridge || 'Non défini'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Group */}
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">Groupe / Classe</label>
                    {editMode ? (
                      <select
                        value={editData.groupe_id}
                        onChange={(e) => setEditData({ ...editData, groupe_id: e.target.value })}
                        className="w-full rounded-md border border-gray-300 p-2"
                        data-testid="edit-groupe"
                      >
                        <option value="">Aucun groupe</option>
                        {groupes.map(g => (
                          <option key={g.id} value={g.id}>{g.nom} ({g.niveau})</option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-[#F5F7FF] rounded-lg">
                        <div className="flex items-center gap-3">
                          <GraduationCap className="h-5 w-5 text-[#285BBB]" />
                          <div>
                            <p className="font-medium text-[#2C3C65]">
                              {selectedEleve.groupe?.nom || 'Non affecté'}
                            </p>
                            {selectedEleve.groupe && (
                              <p className="text-xs text-[#6777A1]">
                                {selectedEleve.groupe.niveau} - {selectedEleve.groupe.horaire}
                              </p>
                            )}
                          </div>
                        </div>
                        {selectedEleve.groupe_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeFromGroupe(selectedEleve.id)}
                            className="text-red-500 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Retirer
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Parents & Inscription info */}
                  {selectedEleve.inscription && (
                    <div className="border-t pt-4" data-testid="parents-section">
                      <h3 className="text-sm font-semibold text-[#2C3C65] mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#285BBB]" />
                        Parents & contact (inscription {selectedEleve.inscription.numero || ''})
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {['pere', 'mere'].map((key) => {
                          const p = selectedEleve.inscription[key];
                          if (!p || (!p.nom && !p.prenom)) return null;
                          return (
                            <div key={key} className="p-3 bg-blue-50 rounded-lg space-y-1">
                              <p className="text-xs font-semibold text-[#285BBB] uppercase">
                                {key === 'pere' ? 'Père' : 'Mère'}
                              </p>
                              <p className="text-sm font-medium text-[#2C3C65]">{p.prenom} {p.nom}</p>
                              {p.telephone1 && (
                                <a
                                  href={`tel:${p.telephone1}`}
                                  className="flex items-center gap-1.5 text-xs text-[#6777A1] hover:text-[#285BBB]"
                                >
                                  <Phone className="h-3 w-3" /> {p.telephone1}
                                  {p.telephone2 && <span className="text-[#6777A1]"> · {p.telephone2}</span>}
                                </a>
                              )}
                              {p.email && (
                                <a
                                  href={`mailto:${p.email}`}
                                  className="flex items-center gap-1.5 text-xs text-[#6777A1] hover:text-[#285BBB] break-all"
                                >
                                  <Mail className="h-3 w-3 flex-shrink-0" /> {p.email}
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {selectedEleve.inscription.adresse && (
                        <div className="mt-3 flex items-start gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-[#285BBB] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-[#6777A1]">Adresse familiale</p>
                            <p className="text-[#2C3C65]">{selectedEleve.inscription.adresse}</p>
                          </div>
                        </div>
                      )}

                      {selectedEleve.inscription.allergies && (
                        <div className="mt-3 flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                          <AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-rose-700 uppercase">Allergies / Particularités médicales</p>
                            <p className="text-sm text-rose-900">{selectedEleve.inscription.allergies}</p>
                          </div>
                        </div>
                      )}

                      {!!(selectedEleve.inscription.personnes_autorisees || []).length && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-[#6777A1] uppercase mb-1">Personnes autorisées</p>
                          <ul className="text-sm text-[#2C3C65] space-y-1">
                            {selectedEleve.inscription.personnes_autorisees.map((p, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <User className="h-3 w-3 text-[#6777A1]" />
                                {p.prenom} {p.nom} ({p.qualite}) — {p.telephone}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <a
                        href={`/admin/inscriptions/${selectedEleve.inscription.id}`}
                        className="inline-flex items-center text-xs text-[#285BBB] hover:underline mt-3"
                      >
                        Voir le dossier d'inscription complet →
                      </a>
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                      <FileText className="inline h-4 w-4 mr-1" />
                      Notes administratives
                    </label>
                    {editMode ? (
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        placeholder="Notes sur l'élève (visibles uniquement par l'administration)..."
                        rows={3}
                        data-testid="edit-notes"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg min-h-[80px]">
                        <p className="text-sm text-[#6777A1]">
                          {selectedEleve.notes || 'Aucune note'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Meta info */}
                  <div className="pt-4 border-t text-xs text-[#6777A1]">
                    <p>Compte créé le: {new Date(selectedEleve.created_at).toLocaleDateString('fr-FR')}</p>
                    <p>Approuvé: {selectedEleve.is_approved ? 'Oui' : 'Non'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[#6777A1]">Sélectionnez un élève pour voir ses détails</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEleves;
